const META_PREFIXES = ['_wpcf7', '_wpnonce', '_wpcf'];

export function formatFieldLabel(key) {
  return String(key)
    .replace(/^_field_/, '')
    .replace(/^your-/, '')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function findKey(keys, predicate) {
  return keys.find(predicate) || null;
}

function pickName(submission, keys) {
  const direct = findKey(keys, k => /^(your-)?(name|full[_-]?name)$/i.test(k));
  if (direct) return submission[direct];

  const firstKey = findKey(keys, k => /^(your-)?first[_-]?name$/i.test(k));
  const lastKey = findKey(keys, k => /^(your-)?last[_-]?name$/i.test(k));
  if (firstKey || lastKey) {
    return [submission[firstKey], submission[lastKey]].filter(Boolean).join(' ').trim() || null;
  }
  return null;
}

function pickByContains(submission, keys, patterns) {
  const k = findKey(keys, key => patterns.some(p => key.toLowerCase().includes(p)));
  return k ? submission[k] : null;
}

function pickMessage(submission, keys) {
  const explicit = keys.find(k => {
    const lower = k.toLowerCase();
    return ['message', 'comments', 'inquiry', 'notes'].some(p => lower.includes(p));
  });
  if (explicit) {
    const v = submission[explicit];
    return v != null ? String(v) : null;
  }

  let longestKey = null;
  let longestLen = 0;
  for (const k of keys) {
    const v = submission[k];
    if (typeof v !== 'string') continue;
    if (v.length > longestLen) {
      longestLen = v.length;
      longestKey = k;
    }
  }
  return longestKey && longestLen > 10 ? submission[longestKey] : null;
}

function isMetaKey(key) {
  if (META_PREFIXES.some(p => key.startsWith(p))) return true;
  if (key.startsWith('_') && !key.startsWith('_field_')) return true;
  return false;
}

export function detectFields(submission, formType) {
  const keys = Object.keys(submission || {});

  const entryIdKey = formType === 'gravity_forms' ? 'id' : 'ID';
  const dateKey = formType === 'gravity_forms' ? 'date_submitted' : 'post_date';

  const entryId = submission[entryIdKey] != null ? String(submission[entryIdKey]) : null;
  const dateRaw = submission[dateKey] || null;
  const date = dateRaw
    ? { iso: String(dateRaw), label: new Date(dateRaw).toLocaleString() }
    : null;

  const usedKeys = new Set();
  if (submission[entryIdKey] !== undefined) usedKeys.add(entryIdKey);
  if (submission[dateKey] !== undefined) usedKeys.add(dateKey);

  const nameValue = pickName(submission, keys);
  if (nameValue) {
    keys.forEach(k => {
      if (/^(your-)?(name|full[_-]?name|first[_-]?name|last[_-]?name)$/i.test(k)) {
        usedKeys.add(k);
      }
    });
  }

  const email = pickByContains(submission, keys.filter(k => !usedKeys.has(k)), ['email']);
  if (email) {
    const k = keys.find(key => !usedKeys.has(key) && key.toLowerCase().includes('email'));
    if (k) usedKeys.add(k);
  }

  const phone = pickByContains(submission, keys.filter(k => !usedKeys.has(k)), ['phone', 'tel', 'mobile']);
  if (phone) {
    const k = keys.find(key => !usedKeys.has(key) && ['phone', 'tel', 'mobile'].some(p => key.toLowerCase().includes(p)));
    if (k) usedKeys.add(k);
  }

  const message = pickMessage(submission, keys.filter(k => !usedKeys.has(k)));
  if (message != null) {
    const k = keys.find(key => !usedKeys.has(key) && submission[key] === message);
    if (k) usedKeys.add(k);
  }

  const rest = keys
    .filter(k => !usedKeys.has(k) && !isMetaKey(k))
    .map(k => {
      const value = submission[k];
      const stringValue = Array.isArray(value) ? value.join(', ') : (value == null ? '' : String(value));
      return {
        key: k,
        label: formatFieldLabel(k),
        value: stringValue,
        isLong: stringValue.includes('\n') || stringValue.length > 100,
      };
    });

  return { entryId, name: nameValue || null, email, phone, message, date, rest };
}
